import { FaCalendarDay, FaCalendarWeek, FaCalendarAlt } from "react-icons/fa";
import { DEFAULT_SECTIONS_ALL_ON, anySectionOn, countActiveSections } from "./reportSections";

/**
 * Otomatik rapor e-postası (`autoReportMailConfig`) — firma ve kullanıcı
 * ayarlarının ortak sabitleri ve yardımcıları. Firma bilgi kutusu, firma
 * çekmecesi ve kullanıcı çekmecesi aynı metni ve aynı kuralı buradan okur.
 */

export const FREQUENCIES = [
  { key: "daily", label: "Günlük", Icon: FaCalendarDay },
  { key: "weekly", label: "Haftalık", Icon: FaCalendarWeek },
  { key: "monthly", label: "Aylık", Icon: FaCalendarAlt },
];

export const FREQ_LABELS = {
  daily: "Günlük",
  weekly: "Haftalık",
  monthly: "Aylık",
};

export const COVERAGE_OPTIONS = {
  daily: [
    { value: "previousDay", label: "Önceki gün" },
    { value: "currentDay", label: "Bugün" },
    { value: "last24Hours", label: "Son 24 saat" },
  ],
  weekly: [
    { value: "previousWeek", label: "Önceki hafta" },
    { value: "currentWeek", label: "Bu hafta" },
    { value: "last7Days", label: "Son 7 gün" },
  ],
  monthly: [
    { value: "previousMonth", label: "Önceki ay" },
    { value: "currentMonth", label: "Bu ay" },
    { value: "last30Days", label: "Son 30 gün" },
  ],
};

const DEFAULT_COVERAGE = {
  daily: "previousDay",
  weekly: "previousWeek",
  monthly: "previousMonth",
};

export const WEEK_DAYS = [
  { id: "mon", label: "Pzt" }, { id: "tue", label: "Sal" }, { id: "wed", label: "Çar" },
  { id: "thu", label: "Per" }, { id: "fri", label: "Cum" }, { id: "sat", label: "Cmt" },
  { id: "sun", label: "Paz" },
];

export const DEFAULT_REPORT_CONFIG = {
  daily: {
    enabled: false,
    sendTime: "08:00",
    coverage: "previousDay",
    sections: { ...DEFAULT_SECTIONS_ALL_ON },
  },
  weekly: {
    enabled: false,
    sendTime: "08:00",
    daysOfWeek: [],
    coverage: "previousWeek",
    sections: { ...DEFAULT_SECTIONS_ALL_ON },
  },
  monthly: {
    enabled: false,
    sendTime: "08:00",
    dayOfMonth: 1,
    coverage: "previousMonth",
    sections: { ...DEFAULT_SECTIONS_ALL_ON },
  },
};

/** Kayıtlı (eksik alanlı olabilir) ayarı tam forma tamamlar. */
export function normalizeReportConfig(cfg) {
  if (!cfg) return DEFAULT_REPORT_CONFIG;
  const out = {};
  for (const { key } of FREQUENCIES) {
    out[key] = {
      ...DEFAULT_REPORT_CONFIG[key],
      ...cfg[key],
      coverage: cfg[key]?.coverage || DEFAULT_COVERAGE[key],
      sections: { ...DEFAULT_SECTIONS_ALL_ON, ...(cfg[key]?.sections || {}) },
    };
    if (key === "weekly") out[key].daysOfWeek = cfg[key]?.daysOfWeek || [];
  }
  return out;
}

/** Tek satırlık özet: "Her gün 08:00 · Önceki gün". */
export function scheduleSummary(key, cfg) {
  const time = cfg?.sendTime || "08:00";
  const coverage = COVERAGE_OPTIONS[key].find((o) => o.value === cfg?.coverage)?.label;
  let when;
  if (key === "daily") when = `Her gün ${time}`;
  else if (key === "weekly") {
    const days = WEEK_DAYS.filter((d) => cfg?.daysOfWeek?.includes(d.id)).map((d) => d.label);
    // Sunucu boş gün listesini "her gün" sayar (scheduler: daysOfWeek $size 0).
    when = days.length ? `${days.join(", ")} · ${time}` : `Her gün ${time}`;
  } else when = `Her ayın ${cfg?.dayOfMonth ?? 1}. günü ${time}`;
  return coverage ? `${when} · ${coverage}` : when;
}

export const sectionsLabel = (cfg) => `${countActiveSections(cfg?.sections)}/4 bölüm`;

export const anyFrequencyEnabled = (cfg) => FREQUENCIES.some(({ key }) => cfg?.[key]?.enabled);

/** Kaydı engelleyen ilk hata mesajı; yoksa null. */
export function reportConfigError(config) {
  for (const { key } of FREQUENCIES) {
    const cfg = config?.[key];
    if (!cfg?.enabled) continue;
    if (!anySectionOn(cfg.sections)) return `${FREQ_LABELS[key]} rapor için en az bir bölüm seçmelisiniz`;
    if (key === "weekly" && !(cfg.daysOfWeek?.length > 0)) return "Haftalık rapor için en az bir gün seçmelisiniz";
  }
  return null;
}
